import { useState } from "react";
import type { GateConditionId } from "@iamu/shared";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Timestamp } from "@/components/ui/timestamp";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CollapsibleSection } from "@/components/collapsible-section";
import {
  BASELINE_MODE_LABELS,
  CONDITION_LABELS,
} from "@/components/gate/gate-vocabulary";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABEL = { error: "Fails the gate", warn: "Warns", off: "Off" };

const STARTER_QUALITY_GATE_YAML = `# iamu/quality-gate.yaml
version: 1
preset: iamu:recommended

# How baseline walkthrough runs are identified for delta comparison
baseline:
  mode: previous_run          # previous_run | previous_build | reference_branch | pinned_build | date | days
  environment: same           # same | any

# Condition overrides (G1–G9)
conditions:
  G1: { level: error, threshold: 0 }    # No new critical problems
  G2: { level: error, threshold: 0 }    # No new major problems
  G3: { level: error, threshold: 0 }    # No critical or major regressions
  G4: { level: error, threshold: 0 }    # No human test fell to No
  G5: { level: warn, threshold: 0 }     # Applied fixes held
  G6: { level: warn, threshold: 0.6 }   # Scenario coverage (0.0 - 1.0)
  G7: { level: warn, threshold: 0 }     # Critical and major baseline problems re-checked
  G8: { level: warn, threshold: 0 }     # No strengths lost
  G9: { level: off, threshold: 0 }      # New minor problems within limit
`;

/**
 * Project settings → Quality gate. Read-only: the config lives in the
 * repo (`iamu/quality-gate.yaml`) and syncs with `iamu sync`.
 */
export function QualityGateSection({ projectId }: { projectId: string }) {
  const { data, isLoading } = trpc.gateConfig.get.useQuery({ projectId });
  const [copied, setCopied] = useState(false);

  if (isLoading || !data) return <Skeleton className="h-40 w-full" />;

  function handleCopy() {
    const textToCopy = data?.sourceYaml || STARTER_QUALITY_GATE_YAML;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      toast.success(
        data?.isDefault
          ? "Starter quality-gate.yaml copied to clipboard"
          : "Quality gate configuration copied to clipboard",
      );
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const overrides = Object.entries(data.config.capabilities);
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {data.isDefault ? (
            <>
              Using the built-in{" "}
              <span className="font-mono">iamu:recommended</span> preset. To
              change it, add{" "}
              <span className="font-mono">iamu/quality-gate.yaml</span> to your
              repository and run <span className="font-mono">iamu sync</span>.
            </>
          ) : (
            <>
              From <span className="font-mono">iamu/quality-gate.yaml</span>,
              synced <Timestamp date={data.updatedAt} />. Edit the file in your
              repository to change it.
            </>
          )}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          className="shrink-0 self-start sm:self-auto"
        >
          {copied ? (
            <>
              <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              {data.isDefault ? "Copy starter YAML" : "Copy YAML config"}
            </>
          )}
        </Button>
      </div>
      <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-[max-content_1fr]">
        <dt className="text-sm text-muted-foreground">Baseline</dt>
        <dd className="text-sm text-foreground">
          {data.config.baseline.mode
            ? BASELINE_MODE_LABELS[data.config.baseline.mode]
            : "Previous run for implicit builds, previous build for labelled ones"}
        </dd>
        <dt className="text-sm text-muted-foreground">Environments</dt>
        <dd className="text-sm text-foreground">
          {data.config.baseline.environment === "any"
            ? "Any environment can be the baseline"
            : "Only runs against the same environment"}
        </dd>
      </dl>
      <Table aria-label="Gate conditions">
        <TableHeader>
          <TableRow>
            <TableHead>Condition</TableHead>
            <TableHead>Level</TableHead>
            <TableHead className="text-right">Threshold</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.conditions.map((c) => (
            <TableRow key={c.id}>
              <TableCell>
                <span className="mr-2 font-mono text-xs text-muted-foreground">
                  {c.id}
                </span>
                {CONDITION_LABELS[c.id as GateConditionId]}
              </TableCell>
              <TableCell>{LEVEL_LABEL[c.level]}</TableCell>
              <TableCell className="text-right tabular-nums">
                {c.id === "G6"
                  ? `${Math.round(c.threshold * 100)}%`
                  : c.threshold}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {overrides.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold">Per-capability overrides</h3>
          <ul className="space-y-1 text-sm">
            {overrides.map(([key, o]) => (
              <li key={key}>
                <span className="font-mono">{key}</span>:{" "}
                {Object.entries(o.conditions)
                  .map(
                    ([id, s]) =>
                      `${id} ${s?.level ?? "inherits level"}${s?.threshold !== undefined ? `, threshold ${s.threshold}` : ""}`,
                  )
                  .join("; ")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.sourceYaml && (
        <CollapsibleSection label="File contents">
          <pre className="overflow-x-auto rounded-md bg-muted p-4 font-mono text-xs text-foreground">
            {data.sourceYaml}
          </pre>
        </CollapsibleSection>
      )}
    </div>
  );
}
